/**
 * WorkerTask (subset) — aligned's `tasks` collection. The expert response lives
 * in `expertAnswer` (JSON of {componentId: value}) and `taskData` (JSON array of
 * task controls). `metadata` is a JSON string that carries the `templateId`.
 */
import type { JobType } from './job';

type Data = Record<string, unknown>;
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const num = (v: unknown, dflt: number): number => (typeof v === 'number' ? v : dflt);
const numOrNull = (v: unknown): number | null => (typeof v === 'number' ? v : null);

// Aligned's WorkerTask.hourlyRate default (MicroUSD(20000000) = $20/hr).
export const DEFAULT_TASK_HOURLY_RATE_MICRO_USD = 20_000_000;

const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

export interface WorkerTask {
  id: string;
  clientTaskId: string;
  jobId: string;
  projectId: string;
  /** Source WorkerTask.jobType; retained for task-local assessment/autorate gates. */
  jobType?: JobType | null;
  /** Original model-comparison messages used by the manager's full and OpenAI JSONL exports. */
  candidate_0?: string | null;
  candidate0Source?: string | null;
  candidate_1?: string | null;
  candidate1Source?: string | null;
  metadata: string | null;
  taskStatus: string;
  /** aligned's `TaskType` enum value (e.g. `'dynamicTemplate'`), stamped by `createTasksNew.js` from the uploaded task JSON's `task_template_id`. */
  taskType: string;
  taskPaymentType: string | null;
  expertAnswer: string | null;
  taskData: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  expertId: string | null;
  expertEmail: string | null;
  firebaseId: string | null;
  hourlyRate: number;
  /** Expert compensation basis. Missing/unknown values map to hourly. */
  expertPayBasis?: 'hourly' | 'perApprovedTask';
  /** Approved Workbench task quantity represented by this accounting task. */
  approvedTaskCount?: number | null;
  /** Campaign task-rate snapshot in micro-USD. */
  approvedTaskRate?: number | null;
  /** Campaign billing-duration snapshot in whole seconds per approved task. */
  billingSecondsPerApprovedTask?: number | null;
  /** Canonical exact expert cost in micro-USD for task-basis records. */
  expertPayMicroUsd?: number | null;
  campaignTaskPayoutRunId?: string | null;
  campaignTaskPayoutLineId?: string | null;
  workWeekStartAt?: string | null;
  workWeekEndAt?: string | null;
  /** Expert pools the task is restricted to; empty = open to any expert. */
  expertPoolIds: string[];
  /** Paying customer; drives platformFee → client_paid_expert_earnings routing. */
  customerId: string | null;
  /** Invoice line item reference. The field is absent on older task records. */
  invoiceItemId?: string | null;
  /** Admin task-management fields retained from the source document. */
  taskPaymentState?: string | null;
  deletedAt?: string | null;
  isAutoRated?: boolean;
  flagged?: boolean;
  skipped?: boolean;
  taskCategory?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  expertEarningsId?: string | null;
  /** Explicit target period on tasks created by the admin Client Payments tool. */
  payPeriodId?: string | null;
  /** Campaign timesheet entry this task was billed from, so voiding the task can
   *  deny the entry it approved. Absent on tasks created before this was stored. */
  timesheetEntryId?: string | null;
  /** Purpose snapshot of `timesheetEntryId`'s row, so invoices and reports can
   *  separate Reviewer time from production time. Null on non-timesheet tasks.
   *  Structurally identical to `CampaignTimesheetPurpose`, restated here because
   *  `shared` does not depend on `domains` (ADR 0001). */
  timesheetPurpose?: 'production' | 'reviewer' | null;
}

export function mapWorkerTask(id: string, data: Data): WorkerTask {
  return {
    id,
    clientTaskId: str(data.clientTaskId) ?? '',
    jobId: str(data.jobId) ?? '',
    projectId: str(data.projectId) ?? '',
    jobType: (str(data.jobType) as JobType | null) ?? null,
    candidate_0: str(data.candidate_0) ?? null,
    candidate0Source: str(data.candidate0Source) ?? null,
    candidate_1: str(data.candidate_1) ?? null,
    candidate1Source: str(data.candidate1Source) ?? null,
    metadata: str(data.metadata) ?? null,
    taskStatus: str(data.taskStatus) ?? 'unreviewed',
    taskType: str(data.taskType) ?? 'dynamicTemplate',
    taskPaymentType: str(data.taskPaymentType) ?? null,
    expertAnswer: str(data.expertAnswer) ?? null,
    taskData: str(data.taskData) ?? null,
    startDateTime: str(data.startDateTime) ?? null,
    endDateTime: str(data.endDateTime) ?? null,
    expertId: str(data.expertId) ?? null,
    expertEmail: str(data.expertEmail) ?? null,
    firebaseId: str(data.firebaseId) ?? null,
    hourlyRate: num(data.hourlyRate, DEFAULT_TASK_HOURLY_RATE_MICRO_USD),
    expertPayBasis: data.expertPayBasis === 'perApprovedTask' ? 'perApprovedTask' : 'hourly',
    approvedTaskCount: numOrNull(data.approvedTaskCount),
    approvedTaskRate: numOrNull(data.approvedTaskRate),
    billingSecondsPerApprovedTask: numOrNull(data.billingSecondsPerApprovedTask),
    expertPayMicroUsd: numOrNull(data.expertPayMicroUsd),
    campaignTaskPayoutRunId: str(data.campaignTaskPayoutRunId) ?? null,
    campaignTaskPayoutLineId: str(data.campaignTaskPayoutLineId) ?? null,
    workWeekStartAt: str(data.workWeekStartAt) ?? null,
    workWeekEndAt: str(data.workWeekEndAt) ?? null,
    expertPoolIds: strList(data.expertPoolIds),
    customerId: str(data.customerId) ?? null,
    invoiceItemId: str(data.invoiceItemId) ?? null,
    taskPaymentState: str(data.taskPaymentState) ?? null,
    deletedAt: str(data.deletedAt) ?? null,
    isAutoRated: data.isAutoRated === true,
    flagged: data.flagged === true,
    skipped: data.skipped === true,
    taskCategory: str(data.taskCategory) ?? null,
    startTime: str(data.startTime) ?? null,
    endTime: str(data.endTime) ?? null,
    expertEarningsId: str(data.expertEarningsId) ?? null,
    payPeriodId: str(data.payPeriodId) ?? null,
    timesheetEntryId: str(data.timesheetEntryId) ?? null,
    timesheetPurpose: data.timesheetPurpose === 'reviewer' || data.timesheetPurpose === 'production'
      ? data.timesheetPurpose
      : null,
  };
}

/** Extract the templateId from a task's `metadata` JSON string. */
export function templateIdFromTask(metadata: string | null): string | null {
  if (!metadata) return null;
  try {
    const parsed = JSON.parse(metadata) as Record<string, unknown>;
    const id = parsed.templateId;
    return typeof id === 'string' ? id : null;
  } catch {
    return null;
  }
}

/**
 * Extract the per-task `initialValues` map from a task's `metadata` JSON string.
 * This is where per-task content lives — chat transcripts, SxS `{chatA,chatB}`
 * payloads, prefilled input values, and `${componentId}_options` overrides —
 * keyed by component id (mirrors aligned's `metadata['initialValues']`, consumed
 * by `DynamicTaskWidgetViewModel`). Returns `{}` when absent or malformed.
 */
export function initialValuesFromTask(metadata: string | null): Record<string, unknown> {
  if (!metadata) return {};
  try {
    const parsed = JSON.parse(metadata) as Record<string, unknown>;
    const iv = parsed.initialValues;
    return iv && typeof iv === 'object' && !Array.isArray(iv) ? (iv as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
